import React, { useState, useEffect, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { defineWarmThemes } from '../components/EditorPanel';
import UpgradeOptions from '../components/UpgradeOptions';
import TerminalPanel from '../components/TerminalPanel';
import { PlayIcon, RefreshIcon } from '../components/Icons';
import { authHeaders } from '../authToken';
import '../bits.css';

/* Jigsaw-piece mark in the brand sage green (var(--brand-green), from the
   brookebit logo). fill=currentColor so the colour comes from CSS. */
function JigsawIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5C13 2.12 11.88 1 10.5 1S8 2.12 8 3.5V5H4c-1.1 0-1.99.9-1.99 2v3.8H3.5c1.49 0 2.7 1.21 2.7 2.7s-1.21 2.7-2.7 2.7H2V20c0 1.1.9 2 2 2h3.8v-1.5c0-1.49 1.21-2.7 2.7-2.7 1.49 0 2.7 1.21 2.7 2.7V22H17c1.1 0 2-.9 2-2v-4h1.5c1.38 0 2.5-1.12 2.5-2.5S21.88 11 20.5 11z" />
    </svg>
  );
}

/*
 * brookebits — a Trinket replacement built on the existing runner.
 *
 * One editor surface, entered in two ways (see BROOKEBITS_PLAN.md):
 *   • Scratchpad — the zero-friction front door. Code lives in localStorage, no
 *     DB row, no quota. Persistence is opt-in: a "bit" is written only when the
 *     user hits Save.
 *   • A bit — a saved, linkable, forkable snippet the user owns.
 *
 * Execution reuses TerminalPanel verbatim: it opens its own authenticated
 * WebSocket to /ws/run, so nothing here touches the run pipeline.
 */

const API_URL = process.env.REACT_APP_API_URL || '';

const STARTER = {
  python:
    '# Scratchpad — write some Python and press Run.\n\n' +
    'print("Hello from brookebit!")\n',
  java:
    '// Scratchpad — write some Java and press Run.\n' +
    '// The class must be named Main.\n\n' +
    'public class Main {\n' +
    '    public static void main(String[] args) {\n' +
    '        System.out.println("Hello from brookebit!");\n' +
    '    }\n' +
    '}\n',
};

const scratchKey = (lang) => `brookebits_scratch_${lang}`;

// Premium upsell shown when a free account tries to save/fork a bit. The Python
// scratchpad (running code) stays free; only persisting is Premium. Reuses the
// shared cd-upsell-* styles. The server is the real boundary; no live checkout yet.
function BitsUpsell({ onClose }) {
  return (
    <div className="cd-upsell-overlay" role="dialog" aria-modal="true" aria-label="Premium" onClick={onClose}>
      <div className="cd-upsell-card" onClick={(e) => e.stopPropagation()}>
        <div className="cd-upsell-icon" aria-hidden="true">🔒</div>
        <h2 className="cd-upsell-title">A Premium feature</h2>
        <p className="cd-upsell-body">
          The Python scratchpad is free to use. Running Java, and saving, forking or
          sharing your own snippets, is part of Premium — along with the full syllabus.
        </p>
        <UpgradeOptions />
        <button className="cd-upsell-btn" onClick={onClose}>Keep using the scratchpad</button>
      </div>
    </div>
  );
}


export default function Bits({ initialBitId, theme, isAdmin = false, entitled = true }) {
  // ── View + list state ---------------------------------------------------
  const [view, setView]         = useState('list');   // 'list' | 'editor'
  const [bits, setBits]         = useState([]);
  const [quota, setQuota]       = useState(null);
  const [used, setUsed]         = useState(0);
  const [loadingList, setLoadingList] = useState(true);

  // ── Editor state --------------------------------------------------------
  const [bitId, setBitId]           = useState(null); // null = unsaved scratch
  const [title, setTitle]           = useState('Untitled bit');
  const [language, setLanguage]     = useState('python');
  const [code, setCode]             = useState('');
  const [visibility, setVisibility] = useState('private');
  const [embedToken, setEmbedToken] = useState(null); // set on load/save for embed bits
  const [isOwner, setIsOwner]       = useState(true);
  const [dirty, setDirty]           = useState(false);
  const [saving, setSaving]         = useState(false);

  const [error, setError]   = useState('');
  const [notice, setNotice] = useState('');
  const [upsellOpen, setUpsellOpen] = useState(false); // Premium gate on save/fork

  // ── List filtering ------------------------------------------------------
  const [search, setSearch]         = useState('');
  const [langFilter, setLangFilter] = useState('all'); // all | python | java
  const [typeFilter, setTypeFilter] = useState('all'); // all | embed | shared | private

  const terminalRef = useRef(null);
  const runRef      = useRef(null);
  const [isRunning, setIsRunning] = useState(false);

  const monacoTheme = theme === 'dark' ? 'ib-warm-dark' : 'ib-warm-light';
  const termBg   = theme === 'dark' ? '#100c08' : '#211c16';
  const termText = theme === 'dark' ? '#f0e8db' : '#f3eadb';

  // ── List loading --------------------------------------------------------
  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await fetch(`${API_URL}/api/bits`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setBits(d.bits || []);
        setQuota(d.quota ?? null);
        setUsed(d.used ?? (d.bits || []).length);
      }
    } catch (_) { /* non-fatal */ }
    setLoadingList(false);
  }, []);

  const openBit = useCallback(async (id) => {
    setError(''); setNotice('');
    try {
      const r = await fetch(`${API_URL}/api/bits/${id}`, { headers: authHeaders() });
      if (!r.ok) {
        setError(r.status === 404 ? 'That bit could not be found.' : 'Could not open that bit.');
        setView('list');
        return;
      }
      const b = await r.json();
      setBitId(b.bit_id);
      setTitle(b.title || 'Untitled bit');
      setLanguage(b.language || 'python');
      setCode(b.code || '');
      setVisibility(b.visibility || 'private');
      setEmbedToken(b.embed_token || null);
      setIsOwner(!!b.is_owner);
      setDirty(false);
      setView('editor');
      window.history.pushState(null, '', `/bits/${b.bit_id}`);
      terminalRef.current?.clear();
    } catch (_) {
      setError('Could not open that bit.');
      setView('list');
    }
  }, []);

  // On mount: open a deep-linked bit, else show the list.
  useEffect(() => {
    if (initialBitId) openBit(initialBitId);
    else fetchList();
  }, [initialBitId, openBit, fetchList]);

  // ── Scratchpad front door ----------------------------------------------
  const newScratch = useCallback((lang) => {
    setError(''); setNotice('');
    let saved = '';
    try { saved = localStorage.getItem(scratchKey(lang)) || ''; } catch (_) {}
    setBitId(null);
    setTitle('Untitled bit');
    setLanguage(lang);
    setCode(saved || STARTER[lang]);
    setVisibility('private');
    setEmbedToken(null);
    setIsOwner(true);
    setDirty(false);
    setView('editor');
    window.history.pushState(null, '', '/bits');
    terminalRef.current?.clear();
  }, []);

  // ── Code / language edits ----------------------------------------------
  const onCodeChange = useCallback((value) => {
    const v = value || '';
    setCode(v);
    setDirty(true);
    // Unsaved scratch is mirrored to localStorage so a refresh loses nothing.
    if (bitId === null) {
      try { localStorage.setItem(scratchKey(language), v); } catch (_) {}
    }
  }, [bitId, language]);

  const onLanguageChange = useCallback((lang) => {
    if (lang === language) return;
    // Java is Premium (heaviest runner workload). Free accounts stay on Python.
    if (lang === 'java' && !entitled) { setUpsellOpen(true); return; }
    setLanguage(lang);
    setDirty(true);
    // In an untouched scratch, swap in the new language's starter template.
    if (bitId === null) {
      const untouched = code.trim() === '' || code === STARTER.python || code === STARTER.java;
      if (untouched) {
        let saved = '';
        try { saved = localStorage.getItem(scratchKey(lang)) || ''; } catch (_) {}
        setCode(saved || STARTER[lang]);
      }
    }
  }, [language, bitId, code, entitled]);

  // ── Run -----------------------------------------------------------------
  const run = useCallback(() => {
    if (isRunning) return;
    terminalRef.current?.startRun(code, language);
  }, [code, language, isRunning]);
  useEffect(() => { runRef.current = run; }, [run]);

  const handleEditorMount = useCallback((editor, monaco) => {
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => runRef.current?.());
  }, []);
  const handleBeforeMount = useCallback((monaco) => { defineWarmThemes(monaco); }, []);

  // ── Save (create on first save, update thereafter) ----------------------
  const save = useCallback(async (overrides = {}) => {
    if (saving) return null;
    const vis = overrides.visibility ?? visibility;
    setSaving(true); setError(''); setNotice('');
    try {
      const creating = bitId === null;
      const r = await fetch(
        creating ? `${API_URL}/api/bits` : `${API_URL}/api/bits/${bitId}`,
        {
          method: creating ? 'POST' : 'PUT',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(
            creating
              ? { title, language, code, visibility: vis }
              : { title, code, visibility: vis }
          ),
        }
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        // Premium gate: show the upsell instead of a raw error. The editor keeps
        // the code (scratch is only cleared on a successful create), so nothing is lost.
        if (r.status === 402 || body.premium) { setUpsellOpen(true); return null; }
        setError(body.error || 'Could not save this bit.');
        return null;
      }
      const b = await r.json();
      setBitId(b.bit_id);
      setVisibility(b.visibility || vis);
      setEmbedToken(b.embed_token || null);
      setIsOwner(true);
      setDirty(false);
      setNotice('Saved.');
      if (creating) {
        // It is a real bit now — drop the browser scratch and claim the URL.
        try { localStorage.removeItem(scratchKey(language)); } catch (_) {}
        window.history.replaceState(null, '', `/bits/${b.bit_id}`);
      }
      return b;
    } catch (_) {
      setError('Could not save this bit.');
      return null;
    } finally {
      setSaving(false);
    }
  }, [saving, bitId, title, language, code, visibility]);

  // ── Fork ----------------------------------------------------------------
  const fork = useCallback(async () => {
    if (!bitId) return;
    setError(''); setNotice('');
    try {
      const r = await fetch(`${API_URL}/api/bits/${bitId}/fork`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        if (r.status === 402 || body.premium) { setUpsellOpen(true); return; }
        setError(body.error || 'Could not fork this bit.');
        return;
      }
      const b = await r.json();
      setNotice('Forked into your bits.');
      openBit(b.bit_id);
    } catch (_) {
      setError('Could not fork this bit.');
    }
  }, [bitId, openBit]);

  // ── Share (make unlisted, copy the link) --------------------------------
  const share = useCallback(async () => {
    setError(''); setNotice('');
    let b = { bit_id: bitId };
    // A share needs a saved, unlisted bit. Save first if needed, flipping
    // visibility to 'unlisted' in the same write (passed explicitly so we don't
    // depend on the async visibility state update landing first).
    if (bitId === null || visibility !== 'unlisted') {
      b = await save({ visibility: 'unlisted' });
      if (!b) return;
    }
    const link = `${window.location.origin}/bits/${b.bit_id}`;
    try {
      await navigator.clipboard.writeText(link);
      setNotice('Share link copied to your clipboard.');
    } catch (_) {
      setNotice(`Share link: ${link}`);
    }
  }, [bitId, visibility, save]);

  // ── Embed (admin only): make embeddable, copy the <iframe> snippet ------
  // Embed authoring is gated server-side to admins; the button is admin-gated
  // too. Making a bit embeddable saves it with visibility='embed', which mints
  // the embed_token the anonymous /embed/<token> page reads.
  const embed = useCallback(async () => {
    setError(''); setNotice('');
    let b = { bit_id: bitId, embed_token: embedToken };
    if (bitId === null || visibility !== 'embed') {
      b = await save({ visibility: 'embed' });
      if (!b) return;
    }
    const token = b.embed_token || embedToken;
    if (!token) { setError('Could not create the embed. Try again.'); return; }
    setEmbedToken(token);
    const src = `${window.location.origin}/embed/${token}`;
    // No sandbox: this is our own trusted page, and framing is already limited
    // to InThinking by the frame-ancestors header. A sandbox would break the
    // same-origin /api/embed fetch and window.prompt-based input().
    const snippet =
      `<iframe src="${src}" width="100%" height="500" ` +
      `style="border:0;border-radius:8px" loading="lazy" ` +
      `title="${(title || 'Interactive code').replace(/"/g, "'")} — brookebit"></iframe>`;
    try {
      await navigator.clipboard.writeText(snippet);
      setNotice('Embed code copied — paste it into your InThinking page.');
    } catch (_) {
      setNotice(`Embed code: ${snippet}`);
    }
  }, [bitId, visibility, embedToken, title, save]);

  const remove = useCallback(async (id) => {
    if (!window.confirm('Delete this bit? This cannot be undone.')) return;
    try {
      const r = await fetch(`${API_URL}/api/bits/${id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (r.ok) {
        if (id === bitId) { setView('list'); setBitId(null); }
        fetchList();
      }
    } catch (_) { /* non-fatal */ }
  }, [bitId, fetchList]);

  const backToList = useCallback(() => {
    setView('list');
    window.history.pushState(null, '', '/bits');
    fetchList();
  }, [fetchList]);

  // ═══════════════════════════════════════════════════════════════════════
  // List view
  // ═══════════════════════════════════════════════════════════════════════
  const q = search.trim().toLowerCase();
  const filteredBits = bits.filter((b) => {
    if (q && !(b.title || '').toLowerCase().includes(q)) return false;
    if (langFilter !== 'all' && b.language !== langFilter) return false;
    if (typeFilter === 'embed'   && b.visibility !== 'embed')    return false;
    if (typeFilter === 'shared'  && b.visibility !== 'unlisted') return false;
    if (typeFilter === 'private' && b.visibility !== 'private')  return false;
    return true;
  });

  if (view === 'list') {
    return (
      <div className="bits-page bits-page-embedded">
        {upsellOpen && <BitsUpsell onClose={() => setUpsellOpen(false)} />}
        <div className="bits-body">
          <section className="bits-hero">
            <h1>Scratchpad</h1>
            <p>Jump straight in and try some code — nothing is saved until you choose to.</p>
            <div className="bits-hero-actions">
              <button className="action-btn action-run-primary" onClick={() => newScratch('python')}>
                <PlayIcon size={11} /> New Python scratchpad
              </button>
              <button
                className="action-btn action-run-secondary"
                onClick={entitled ? () => newScratch('java') : () => setUpsellOpen(true)}
                title={entitled ? undefined : 'Java is a Premium feature'}
              >
                {entitled ? <PlayIcon size={11} /> : <span aria-hidden="true">🔒</span>} New Java scratchpad
              </button>
            </div>
          </section>

          <section className="bits-list-section">
            <h2>
              My bits
              {quota != null && <span className="bits-quota-badge">{used} / {quota}</span>}
            </h2>
            {!loadingList && bits.length > 0 && (
              <div className="bits-filters">
                <input
                  className="bits-search"
                  type="search"
                  placeholder="Search by title…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search bits by title"
                />
                <div className="bits-filter-group" role="group" aria-label="Filter by language">
                  {['all', 'python', 'java'].map((v) => (
                    <button
                      key={v}
                      className={`bits-chip ${langFilter === v ? 'is-active' : ''}`}
                      onClick={() => setLangFilter(v)}
                    >
                      {v === 'all' ? 'All' : v === 'python' ? 'Python' : 'Java'}
                    </button>
                  ))}
                </div>
                <div className="bits-filter-group" role="group" aria-label="Filter by type">
                  {['all', 'embed', 'shared', 'private'].map((v) => (
                    <button
                      key={v}
                      className={`bits-chip ${typeFilter === v ? 'is-active' : ''}`}
                      onClick={() => setTypeFilter(v)}
                    >
                      {v === 'all' ? 'All' : v === 'embed' ? '⧉ Embeds' : v === 'shared' ? '🔗 Shared' : 'Private'}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {loadingList ? (
              <p className="bits-empty">Loading…</p>
            ) : bits.length === 0 ? (
              <p className="bits-empty">
                No saved bits yet. Open a scratchpad above, then press <strong>Save</strong> to keep it.
              </p>
            ) : filteredBits.length === 0 ? (
              <p className="bits-empty">No bits match your search or filters.</p>
            ) : (
              <div className="bits-grid">
                {filteredBits.map((b) => (
                  <div key={b.bit_id} className="bit-card" onClick={() => openBit(b.bit_id)}>
                    <div className="bit-card-head">
                      <span className={`bit-lang bit-lang-${b.language}`}>
                        {b.language === 'java' ? 'Java' : 'Python'}
                      </span>
                      {b.visibility === 'embed'
                        ? <span className="bit-shared bit-shared-embed">⧉ embed</span>
                        : b.visibility === 'unlisted' && <span className="bit-shared">🔗 shared</span>}
                    </div>
                    <div className="bit-card-title">{b.title}</div>
                    <div className="bit-card-meta">
                      {b.forked_from ? 'forked · ' : ''}edited {formatDate(b.updated_at)}
                    </div>
                    <button
                      className="bit-card-del"
                      title="Delete bit"
                      onClick={(e) => { e.stopPropagation(); remove(b.bit_id); }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Editor view
  // ═══════════════════════════════════════════════════════════════════════
  const filename = language === 'java' ? 'Main.java' : 'main.py';
  const unsaved  = bitId === null;

  return (
    <div className="bits-page bits-page-embedded">
      {upsellOpen && <BitsUpsell onClose={() => setUpsellOpen(false)} />}
      <header className="bits-topbar">
        <button className="bits-back" onClick={backToList}>‹ My bits</button>
        <input
          className="bits-title-input"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
          disabled={!isOwner}
          aria-label="Bit title"
        />
        <div className="bits-topbar-right">
          <select
            className="bits-lang-select"
            value={language}
            onChange={(e) => onLanguageChange(e.target.value)}
            disabled={!isOwner && !unsaved}
            aria-label="Language"
          >
            <option value="python">Python</option>
            <option value="java" disabled={!entitled}>{entitled ? 'Java' : 'Java 🔒'}</option>
          </select>
        </div>
      </header>

      {error  && <div className="bits-flash bits-flash-error">{error}</div>}
      {notice && <div className="bits-flash bits-flash-ok">{notice}</div>}

      <div className="bits-editor-grid">
        {/* Editor */}
        <div className="card editor-card bits-editor-card">
          <div className="editor-header card-header">
            <span className="editor-filename">{filename}</span>
            {!isOwner && <span className="bits-readonly-badge">read-only · fork to edit</span>}
            {isOwner && visibility === 'embed' && (
              <span className="bit-embed-badge" title="This bit is embeddable on external sites">⧉ embeddable</span>
            )}
            {isOwner && dirty && <span className="bits-dirty-dot" title="Unsaved changes">●</span>}
            <a className="bits-logo-badge" href="https://brookebit.com" target="_blank" rel="noreferrer" title="Made with brookebit">
              <JigsawIcon />
            </a>
          </div>
          <div className="editor-monaco-wrap">
            <Editor
              height="100%"
              language={language}
              value={code}
              onChange={onCodeChange}
              theme={monacoTheme}
              beforeMount={handleBeforeMount}
              onMount={handleEditorMount}
              options={{
                readOnly: !isOwner,
                fontFamily: '"JetBrains Mono", "Cascadia Code", "Fira Code", Consolas, "Courier New", monospace',
                fontLigatures: true,
                fontSize: 13,
                lineHeight: Math.round(1.65 * 13),
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                wordWrap: 'off',
                automaticLayout: true,
                padding: { top: 14, bottom: 14 },
                smoothScrolling: true,
                cursorBlinking: 'smooth',
                renderLineHighlight: 'line',
              }}
            />
          </div>

          {/* Action bar */}
          <div className="action-bar bits-action-bar">
            <div className="bits-action-left">
              <button className="action-btn action-run-secondary" onClick={run} disabled={isRunning}>
                {isRunning ? <>⏳ Running…</> : <><PlayIcon size={10} /> Run</>}
              </button>
            </div>
            <div className="bits-action-right">
              {isOwner ? (
                <>
                  <button className="action-btn action-reset" onClick={share} title="Copy a shareable link">
                    🔗 Share
                  </button>
                  {isAdmin && (
                    <button
                      className="action-btn action-reset"
                      onClick={embed}
                      title="Make embeddable and copy the iframe code for InThinking"
                    >
                      {visibility === 'embed' ? '⧉ Copy embed' : '⧉ Embed'}
                    </button>
                  )}
                  <button
                    className="action-btn action-run-primary"
                    onClick={entitled ? save : () => setUpsellOpen(true)}
                    disabled={entitled && (saving || (!dirty && !unsaved))}
                    title={entitled ? undefined : 'Saving snippets is a Premium feature'}
                  >
                    {saving
                      ? <>⏳ Saving…</>
                      : <>{entitled ? <RefreshIcon size={11} /> : <span aria-hidden="true">🔒</span>} {unsaved ? 'Save' : 'Update'}</>}
                  </button>
                </>
              ) : (
                <button
                  className="action-btn action-run-primary"
                  onClick={entitled ? fork : () => setUpsellOpen(true)}
                  title={entitled ? 'Copy this bit into your account' : 'Forking is a Premium feature'}
                >
                  {entitled ? '⑂' : '🔒'} Fork to edit
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Terminal */}
        <div className="bits-terminal-col">
          <TerminalPanel
            ref={terminalRef}
            onRunStateChange={setIsRunning}
            termBg={termBg}
            termText={termText}
          />
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (_) { return ''; }
}
